import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Lock } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="login-page"
            className="min-h-screen flex items-center justify-center bg-sage px-6"
        >
            <Seo title="Admin Login — DentAssists" description="Secure admin login for the DentAssists leads dashboard." path="/admin/login" noindex />
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2.5 justify-center mb-10">
                    <img src="/logo.png" alt="DentAssists logo" className="w-12 h-12 rounded-full" />
                    <span className="font-heading text-2xl text-cream">DentAssists</span>
                </Link>

                <div className="bg-cream rounded-sm border border-linec p-9">
                    <div className="flex items-center gap-2 text-inksoft mb-2">
                        <Lock size={15} />
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                            Admin access
                        </span>
                    </div>
                    <h1 className="font-heading text-3xl text-ink">Sign in</h1>

                    <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="login-form">
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft mb-1 block">
                                Email
                            </label>
                            <input
                                type="email"
                                required
                                data-testid="login-email-input"
                                className="w-full bg-transparent border-b border-linec py-3 outline-none text-ink transition-colors focus:border-terracotta"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft mb-1 block">
                                Password
                            </label>
                            <input
                                type="password"
                                required
                                data-testid="login-password-input"
                                className="w-full bg-transparent border-b border-linec py-3 outline-none text-ink transition-colors focus:border-terracotta"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        {error && (
                            <p data-testid="login-error" className="text-sm text-destructive">
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="inline-flex items-center justify-center gap-2 bg-sage text-cream px-6 py-3.5 rounded-sm font-medium mt-2 transition-[background-color] duration-200 hover:bg-sagedark disabled:opacity-60"
                        >
                            {loading && <Loader2 size={16} className="animate-spin" />}
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                    </form>
                </div>
                <Link
                    to="/"
                    className="block text-center text-cream/60 text-sm mt-6 hover:text-cream transition-colors"
                >
                    ← Back to site
                </Link>
            </div>
        </div>
    );
}
